import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Database, Check, X } from 'lucide-react';

const FixDatabasePage = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  const fixDatabase = async () => {
    if (!supabase) {
      setResult({ success: false, message: 'Supabase client not available' });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      console.log('Adding user to app_users table...');

      // Insert the user into app_users table
      const { data, error } = await supabase
        .from('app_users')
        .upsert({
          email: 'george.mcpherson@rightawaygroup.com',
          full_name: 'George McPherson',
          role: 'admin'
        })
        .select();

      if (error) {
        throw error;
      }

      console.log('Database record created:', data);

      setResult({
        success: true,
        message: 'Database record created successfully! You can now login.'
      });

    } catch (error: any) {
      console.error('Error fixing database:', error);
      setResult({
        success: false,
        message: `Error: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white p-8">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">🔧 Fix Database Record</h1>

        <div className="bg-slate-900 rounded-lg p-6 border border-slate-800">
          <div className="text-center mb-6">
            <Database className="w-16 h-16 mx-auto mb-4 text-green-500" />
            <h2 className="text-xl font-semibold mb-2">Add User to Database</h2>
            <p className="text-slate-400">
              Auth user exists, but database record is missing. This will fix it.
            </p>
          </div>

          <div className="bg-slate-800 rounded p-4 mb-6">
            <h3 className="font-semibold mb-2">What this will do:</h3>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>✅ Add <strong>george.mcpherson@rightawaygroup.com</strong> to app_users table</li>
              <li>✅ Set role to <strong>admin</strong></li>
              <li>✅ Enable login access</li>
            </ul>
          </div>

          {result && (
            <div className={`rounded p-4 mb-6 ${
              result.success
                ? 'bg-green-900/20 border border-green-700'
                : 'bg-red-900/20 border border-red-700'
            }`}>
              <div className="flex items-center gap-2">
                {result.success ? (
                  <Check className="w-5 h-5 text-green-400" />
                ) : (
                  <X className="w-5 h-5 text-red-400" />
                )}
                <p className={result.success ? 'text-green-100' : 'text-red-100'}>
                  {result.message}
                </p>
              </div>
            </div>
          )}

          <div className="flex gap-4">
            <button
              onClick={fixDatabase}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-lg font-semibold transition-colors"
            >
              {loading ? (
                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <Database className="w-5 h-5" />
              )}
              {loading ? 'Fixing...' : 'Fix Database'}
            </button>

            <a
              href="/admin/login"
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold transition-colors"
            >
              Go to Login
            </a>
          </div>
        </div>

        <div className="text-center mt-6">
          <a
            href="/admin/test"
            className="text-slate-400 hover:text-slate-300 transition-colors"
          >
            ← Back to Debug Tests
          </a>
        </div>
      </div>
    </div>
  );
};

export default FixDatabasePage;